// Animated replay of a putt, ported from puttr-app/components/common/PuttReplay.js:
// the ball rolls along the recorded path with a trail growing behind it. Points
// are uniform time samples from the camera, so linear progress over the point
// index reproduces the real ball speed, including deceleration. Calls onDone
// when the roll finishes; the modal then swaps in the static PuttPath, which
// shares computePathGeometry so the two line up exactly.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { computePathGeometry } from './puttDisplay';

export default function PuttReplay({ d, width = 432, onDone }) {
  const { made } = d;
  const geo = useMemo(() => computePathGeometry(d, width), [d, width]);
  const { gw, gh, holeX, holeY, holeRad, points, radii } = geo;
  const n = points.length;
  const [progress, setProgress] = useState(0);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const uid = React.useId().replace(/:/g, '');

  useEffect(() => {
    if (n < 2) {
      if (doneRef.current) doneRef.current();
      return undefined;
    }
    const duration = Math.min(1800, Math.max(700, n * 30));
    let raf = 0;
    let start = null;
    const tick = (now) => {
      if (start == null) start = now;
      const p = Math.min((now - start) / duration, 1);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else if (doneRef.current) doneRef.current();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // Mount-only: plays once per mount; re-runs are driven by key changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Everything below derives from progress in [0, 1] over the point index.
  const t = Math.min(Math.max(progress, 0), 1) * Math.max(n - 1, 0);
  const i = Math.min(Math.floor(t), Math.max(n - 1, 0));
  const f = t - i;

  let trail = '';
  let ballX = 0;
  let ballY = 0;
  let ballR = 0;
  if (n > 0) {
    const a = points[i];
    const b = points[Math.min(i + 1, n - 1)];
    ballX = a[0] + (b[0] - a[0]) * f;
    ballY = a[1] + (b[1] - a[1]) * f;
    const ra = radii[i];
    const rb = radii[Math.min(i + 1, n - 1)];
    // Radius comes from the recorded data, so made putts naturally shrink as
    // the ball drops into the cup.
    ballR = Math.max(3.5, (ra + (rb - ra) * f) * 0.45);

    trail = `M${points[0][0]} ${points[0][1]}`;
    for (let k = 1; k <= i; k++) trail += ` L${points[k][0]} ${points[k][1]}`;
    if (i < n - 1 && f > 0) trail += ` L${ballX} ${ballY}`;
  }

  const outcome = made ? '#2D8C3C' : '#FF5252';

  return (
    <div className="puttPathBox">
      <svg viewBox={`0 0 ${gw} ${gh}`} width="100%" style={{ display: 'block' }}>
        <defs>
          <linearGradient id={`trailGrad${uid}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#fff" stopOpacity="0.15" />
            <stop offset="1" stopColor="#fff" stopOpacity="0.9" />
          </linearGradient>
          <radialGradient id={`replayGlow${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor={outcome} stopOpacity="0.3" />
            <stop offset="1" stopColor={outcome} stopOpacity="0" />
          </radialGradient>
        </defs>

        {[0.25, 0.5, 0.75].map(pct => (
          <line key={`h${pct}`} x1={0} y1={gh * pct} x2={gw} y2={gh * pct} stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        ))}
        {[0.25, 0.5, 0.75].map(pct => (
          <line key={`v${pct}`} x1={gw * pct} y1={0} x2={gw * pct} y2={gh} stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        ))}

        <circle cx={holeX} cy={holeY} r={holeRad * 2.5} fill={`url(#replayGlow${uid})`} />
        <circle cx={holeX} cy={holeY} r={holeRad + 2} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
        <circle cx={holeX} cy={holeY} r={holeRad} fill="rgba(0,0,0,0.6)" stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} />

        {trail && (
          <path d={trail} fill="none" stroke={`url(#trailGrad${uid})`} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
        )}
        {n > 0 && <circle cx={ballX} cy={ballY} r={ballR} fill="#fff" />}
      </svg>
    </div>
  );
}
